/**
 * Last session's same set, for the compact workout screens (#4142 review fixes, R4): the "Last
 * session" line above an open set's inputs and the "Last time" line in a done set's details, for
 * set 2 and later (and for a set 1 that is not a benchmark set).
 *
 * Pure and Angular-free. Display only. The rules: `design-docs/working-set-screen.md`, "Last
 * session's set N".
 */
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { getRepGainAtSameWeight, readWeightAndReps, WeightAndReps } from './set-progress-display';

/**
 * The weight and reps of each set of an earlier workout's exercise, by set index. An entry is
 * `undefined` when that set is not Done (skipped or unfinished) or has no finite weight and reps.
 * Myo-rep sets read their main `stats`. A list that is not an array gives an empty list.
 */
export function readLastSessionSetStats(sets: unknown): (WeightAndReps | undefined)[] {
  if (!Array.isArray(sets)) {
    return [];
  }
  return sets.map(set => {
    if (!set || typeof set !== 'object' || (set as { state?: unknown }).state !== LogRecordState.Done) {
      return undefined;
    }
    return readWeightAndReps((set as { stats?: unknown }).stats);
  });
}

/** Last session's set at `index`, or `undefined` when last time had fewer sets or that one is unusable. */
export function getLastSessionSet(lastSets: readonly (WeightAndReps | undefined)[] | undefined, index: number): WeightAndReps | undefined {
  return lastSets?.[index];
}

/** What a later done set's details show about last session's same set. */
export interface LaterSetComparison {
  /** "Last time <weight × reps>"; absent when unavailable */
  lastTime?: WeightAndReps;
  /** "+N reps (vs. last time)": only when this set beat that set at the same weight */
  overLastTime?: number;
}

/** Compares a done set with last session's same set. */
export function compareWithLastSessionSet(done: WeightAndReps | undefined, last: WeightAndReps | undefined): LaterSetComparison {
  const comparison: LaterSetComparison = {};
  if (last) {
    comparison.lastTime = last;
  }
  const gain = getRepGainAtSameWeight(done, last);
  if (gain !== undefined) {
    comparison.overLastTime = gain;
  }
  return comparison;
}
